import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { PriceDTO } from '@bifeai/shared';
import { Badge, Button, Callout, Card, CardBody, CardHeader, Dialog, Field, Input, PageHeader, Skeleton, useToast } from '@bifeai/ui';
import { Tags } from 'lucide-react';
import { useState } from 'react';
import { api, errorMessage } from '../lib/api';
import { useCan } from '../lib/session';
import { formatDateTime, formatUsd } from '../lib/format';

/**
 * Precios de BifeAI. Se crea el precio en Stripe (Productos → Agregar precio) y se pega acá su ID.
 * El monto se lee de Stripe; la web y el sistema lo muestran al instante.
 */
export function PricesPage() {
  const prices = useQuery({ queryKey: ['billing', 'prices'], queryFn: () => api.get<PriceDTO[]>('/platform/billing/prices') });
  const [editing, setEditing] = useState<PriceDTO | null>(null);
  const can = useCan();
  const missing = (prices.data ?? []).filter((p) => !p.stripePriceId).length;

  return (
    <>
      <PageHeader title="Precios" subtitle="Lo que cobra BifeAI. Creá el precio en Stripe y pegá su ID: la web y el sistema lo toman al instante." />
      {missing > 0 && (
        <Callout tone="warning">
          Faltan {missing} {missing === 1 ? 'precio' : 'precios'} por conectar con Stripe. Hasta que estén todos, la compra online en bifeai.com no se
          habilita.
        </Callout>
      )}
      <Card>
        <CardHeader title="Lista de precios" subtitle="Pago único: licencias y adicionales. Anual: mantenimiento (desde el segundo año). Mensual: bifi y el abono del sistema (en vez de la licencia)." />
        <CardBody flush>
          {prices.isPending ? (
            <Skeleton style={{ height: 200 }} />
          ) : (
            <div className="bf-table-wrap">
              <table className="bf-table">
                <thead>
                  <tr>
                    <th>Qué</th>
                    <th>Tipo</th>
                    <th style={{ textAlign: 'right' }}>Precio</th>
                    <th>ID en Stripe</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {prices.data?.map((p) => (
                    <tr key={p.key}>
                      <td>
                        <span className="bf-cell-main">{p.label}</span>
                        {p.updatedAt && <div className="bf-cell-sub">Cambiado {formatDateTime(p.updatedAt)}</div>}
                      </td>
                      <td>{p.recurring ? <Badge tone="info">{monthly(p.key) ? 'Mensual' : 'Anual'}</Badge> : <Badge>Pago único</Badge>}</td>
                      <td className="bf-num" style={{ textAlign: 'right' }}>
                        {formatUsd(p.amountCents)}
                        {p.recurring ? (monthly(p.key) ? ' /mes' : ' /año') : ''}
                      </td>
                      <td className="bf-cell-sub" style={{ fontFamily: 'monospace' }}>
                        {p.stripePriceId ?? <Badge tone="warning">Sin conectar</Badge>}
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        {can('prices.manage') && (
                          <Button size="sm" icon={<Tags />} onClick={() => setEditing(p)}>
                            Cambiar
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>
      {editing && <PriceDialog price={editing} onClose={() => setEditing(null)} />}
    </>
  );
}

function PriceDialog({ price, onClose }: { price: PriceDTO; onClose: () => void }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [id, setId] = useState('');
  const [apply, setApply] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function save() {
    setBusy(true);
    setError(null);
    try {
      const r = await api.put<{ price: PriceDTO; updatedClients: number }>(`/platform/billing/prices/${price.key}`, {
        stripePriceId: id.trim(),
        applyToExisting: price.recurring ? apply : false,
      });
      await qc.invalidateQueries({ queryKey: ['billing'] });
      toast.success(
        `${price.label}: ${formatUsd(r.price.amountCents)}`,
        r.updatedClients ? `Se aplica a ${r.updatedClients} clientes en su próxima renovación.` : 'Rige para las compras nuevas.',
      );
      onClose();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog
      open
      onOpenChange={(o) => !o && !busy && onClose()}
      title={`Cambiar precio: ${price.label}`}
      description={`Hoy: ${formatUsd(price.amountCents)}${price.recurring ? (monthly(price.key) ? ' por mes' : ' por año') : ''}. En Stripe: Productos → este producto → Agregar precio${
        price.recurring ? (monthly(price.key) ? ' (recurrente, mensual)' : ' (recurrente, anual)') : ' (pago único)'
      }, en USD. Copiá el ID (price_…) y pegalo acá.`}
    >
      <div className="bf-form">
        {error && <Callout tone="danger">{error}</Callout>}
        <Field label="ID del precio en Stripe">
          <Input value={id} onChange={(e) => setId(e.target.value)} placeholder="price_…" autoFocus spellCheck={false} />
        </Field>
        {price.recurring && !monthly(price.key) && (
          <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', fontSize: 14.5 }}>
            <input type="checkbox" checked={apply} onChange={(e) => setApply(e.target.checked)} style={{ marginTop: 4 }} />
            <span>
              Aplicarlo también a los clientes actuales en su próxima renovación.{' '}
              <span className="bf-cell-sub">Avisales con 30 días de anticipación (lo dicen los términos).</span>
            </span>
          </label>
        )}
        <div className="bf-form-actions">
          <Button onClick={onClose} disabled={busy}>
            Cancelar
          </Button>
          <Button variant="primary" loading={busy} disabled={!id.trim().startsWith('price_')} onClick={() => void save()}>
            Guardar
          </Button>
        </div>
      </div>
    </Dialog>
  );
}

/** Los abonos (bifi y el abono del sistema) se cobran por mes; el mantenimiento, por año. */
function monthly(key: string): boolean {
  return key.startsWith('BIFI_MONTHLY') || key.startsWith('SUB_');
}
